import { useMemo } from "react";
import { z } from "zod";

import PieDonutChart from "@/components/ui/pie-chart/PieDonutChart";
import { buildPieChartCatalogModel } from "@/components/ui/pie-chart/pieChartUtils";
import { chartHexColorSchema } from "@/lib/utils/chartUtils";

export const ChatPieDonutChartSpec = {
  props: z.object({
    title: z.string().nullable().optional(),
    data: z.array(z.record(z.string(), z.unknown())),
    nameKey: z.string(),
    valueKey: z.string(),
    height: z.number().nullable().optional(),
    /** Degrees of gap between slices; omit or null for default (2). */
    paddingAngle: z.number().nullable().optional(),
    colors: z.record(z.string(), chartHexColorSchema).nullable().optional(),
  }),
  description:
    "Pie/donut chart for proportional breakdowns (sentiment mix, category split). Props: title (string or null), data (array of records), nameKey (segment label field), valueKey (numeric measure field), height (px or null for auto), colors (optional map of segment nameKey value → #RGB or #RRGGBB hex for slice fills and legend swatches), paddingAngle (degrees between slices; omit or null for default 2). When colors is omitted, segments whose labels contain positive, negative, or neutral (case-insensitive) get semantic sentiment CSS variables; other labels use var(--chart-1) through var(--chart-5) by index. Order sentiment segments Positive → Neutral → Negative. Bind read-only tool data with $state paths on props when needed.",
  example: {
    title: "Coverage by sentiment",
    data: [
      { sentiment: "Positive", articles: 184 },
      { sentiment: "Neutral", articles: 97 },
      { sentiment: "Negative", articles: 31 },
    ],
    nameKey: "sentiment",
    valueKey: "articles",
    height: 280,
  },
} as const;

/** Parses props and renders {@link PieDonutChart}. */
export const ChatPieDonutChartFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => {
  const model = useMemo(() => {
    const parsed = ChatPieDonutChartSpec.props.safeParse(props);
    if (!parsed.success) return null;

    const { title, data, nameKey, valueKey, height, paddingAngle, colors } =
      parsed.data;

    return {
      title: title ?? null,
      nameKey,
      valueKey,
      height: height ?? null,
      paddingAngle: paddingAngle ?? undefined,
      ...buildPieChartCatalogModel({
        data,
        nameKey,
        valueKey,
        colors: colors ?? null,
      }),
    };
  }, [props]);

  if (model == null) return null;

  return (
    <PieDonutChart
      title={model.title}
      data={model.chartData}
      chartConfig={model.chartConfig}
      dataKey={model.valueKey}
      nameKey={model.nameKey}
      height={model.height}
      paddingAngle={model.paddingAngle}
    />
  );
};
